import { useEffect, useState } from "react";
import {
  buildReportDateRangeFromDates,
  getDefaultPdfReportDates,
  getPdfReportQuickDates,
} from "../utils/energyReportData";

const PDF_QUICK_RANGES = [
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
  { id: "3m", label: "3M" },
];

export default function EnergyReportPdfDialog({
  isOpen,
  onClose,
  onGenerate,
  loading,
  scopeLabel,
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState("7d");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const defaults = getDefaultPdfReportDates();
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setActivePreset(defaults.preset || "7d");
    setError("");
  }, [isOpen]);

  const applyQuickRange = (presetId) => {
    const range = getPdfReportQuickDates(presetId);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setActivePreset(presetId);
    setError("");
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    const range = buildReportDateRangeFromDates(startDate, endDate);
    if (!range) {
      setError("Choose a valid from/to date range (from must be on or before to).");
      return;
    }
    setError("");
    onGenerate(range);
  };

  const todayStr = getDefaultPdfReportDates().endDate;

  return (
    <div
      className="pdf-report-dialog-backdrop"
      onClick={loading ? undefined : onClose}
      role="presentation"
    >
      <div
        className="pdf-report-dialog-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-report-dialog-title"
      >
        <div className="pdf-report-dialog-header">
          <h3 id="pdf-report-dialog-title">Generate Energy Report</h3>
          <button
            type="button"
            className="pdf-report-dialog-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <p className="pdf-report-dialog-scope">
          Scope: <strong>{scopeLabel || "Whole Facility (Usage)"}</strong>
        </p>

        {error ? <div className="pdf-report-dialog-error">{error}</div> : null}

        <div className="pdf-report-dialog-quick">
          {PDF_QUICK_RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              className={`pdf-report-dialog-chip${
                activePreset === range.id ? " is-active" : ""
              }`}
              onClick={() => applyQuickRange(range.id)}
              disabled={loading}
            >
              {range.label}
            </button>
          ))}
        </div>

        <div className="pdf-report-dialog-fields">
          <label className="pdf-report-dialog-field">
            <span>From date</span>
            <input
              type="date"
              value={startDate}
              max={endDate || todayStr}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset("");
              }}
              disabled={loading}
            />
          </label>
          <label className="pdf-report-dialog-field">
            <span>To date</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayStr}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset("");
              }}
              disabled={loading}
            />
          </label>
        </div>

        <p className="pdf-report-dialog-hint">
          Report uses full days only (00:00 to 23:59). Scope comes from the
          machine selected on this tab.
        </p>

        <div className="pdf-report-dialog-actions">
          <button
            type="button"
            className="pdf-report-dialog-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pdf-report-dialog-submit"
            onClick={handleSubmit}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? "Generating PDF…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
